import React, { useState, useRef, useEffect } from 'react';
import { TAP_TARGET_PHONE } from '../constants/buttons';

const LS_KEY_PREFIX = 'sfx_hiddenCols_';

// The panel's minimum width (its `min-w-[200px]`) and the gap kept from the viewport edge —
// used to decide whether the panel must open from the gear's LEFT edge.
export const COLUMN_MENU_MIN_WIDTH_PX = 200;
export const COLUMN_MENU_EDGE_PAD_PX = 8;

/**
 * Hook to manage column visibility per section, persisted to localStorage.
 * @param {string} sectionId - unique ID for the section
 * @param {Array<{key: string, label: string}>} columns - available columns
 * @returns {{ isVisible: (key: string) => boolean, hiddenColumns: Set, toggleColumn: (key: string) => void, columns: Array }}
 */
export function useColumnVisibility(sectionId, columns) {
  const [hiddenColumns, setHiddenColumns] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_KEY_PREFIX + sectionId);
      if (saved) return new Set(JSON.parse(saved));
    } catch { /* ignore */ }
    return new Set();
  });

  const isVisible = (key) => !hiddenColumns.has(key);

  const toggleColumn = (key) => {
    setHiddenColumns(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      try {
        localStorage.setItem(LS_KEY_PREFIX + sectionId, JSON.stringify([...next]));
      } catch { /* ignore */ }
      return next;
    });
  };

  return { isVisible, hiddenColumns, toggleColumn, columns };
}

/**
 * Gear icon button that opens a dropdown to toggle column visibility.
 */
export default function ColumnCustomizer({ columns, isVisible, toggleColumn }) {
  const [open, setOpen] = useState(false);
  const [alignLeft, setAlignLeft] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          // 🪤 OFF-SCREEN (2026-10-28, found by e2e-local/menus-on-screen.spec.js at 390px on
          // Admin Settings): the panel hangs LEFT from the gear's right edge at min 200px, and on a
          // phone the gear can sit within 200px of the LEFT edge, so the panel started at x=-68.
          // Open from the gear's LEFT edge instead when there is no room — SharePdfButton's flip,
          // mirrored.
          const r = e.currentTarget.getBoundingClientRect();
          setAlignLeft(r.right - COLUMN_MENU_MIN_WIDTH_PX < COLUMN_MENU_EDGE_PAD_PX);
          setOpen(!open);
        }}
        className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 ${TAP_TARGET_PHONE}`}
        title="Customize columns"
        aria-label="Customize columns"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="column-customizer-btn"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </button>
      {open && (
        <div
          data-align={alignLeft ? 'left' : 'right'}
          className={`absolute ${alignLeft ? 'left-0' : 'right-0'} top-full mt-1 max-w-[calc(100vw-1rem)] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 min-w-[200px] py-2`}
          onClick={(e) => e.stopPropagation()}
          data-testid="column-customizer-dropdown"
        >
          <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-100 dark:border-gray-700 mb-1">
            Show/Hide Columns
          </div>
          {columns.map(col => (
            <label
              key={col.key}
              className="flex items-center px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm text-gray-700 dark:text-gray-200"
            >
              <input
                type="checkbox"
                checked={isVisible(col.key)}
                onChange={() => toggleColumn(col.key)}
                className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              {col.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
